import { config } from '../config/environment';
import { alertManager } from './AlertManager';
import { debugWarn, debugLog } from '../utils/debug';
import { apiStatusManager } from './ApiStatusManager';
import { appBuildManager } from './AppBuildManager';
import { isReplayableRequest } from './nonReplayableEndpoints';
import { siteGateManager, SITE_GATE_STATUS } from './siteGateManager';
import { logError } from './errorLoggingService';
import { getDeviceId, getTabId } from '../utils/clientIdentifiers';
import { TEAM_BILLING_READ_ONLY_CODE, dispatchTeamBillingReadOnly } from '../utils/teamBillingReadOnly';

/**
 * Type for token refresh function
 */
export type TokenRefreshFunction = () => Promise<boolean>;

/**
 * Base API service class that provides common functionality for all API services
 */
export abstract class BaseApiService {
    protected readonly apiBaseUrl: string;
    private readonly tokenRefreshFn: TokenRefreshFunction;
    private static refreshInProgress: boolean = false;
    private static refreshPromise: Promise<boolean> | null = null;

    /** Optional handler for 403 responses (e.g. refresh auth state). Set by AuthApiService to avoid circular import. */
    private static _on403Handler: (() => Promise<void>) | null = null;
    static setOn403Handler(fn: () => Promise<void>): void {
        BaseApiService._on403Handler = fn;
    }

    /** Handler for 401s caused by a team billing/subscription change. Set by App to show the team-updated modal. */
    private static _onTeamUpdateRevocationHandler: (() => void) | null = null;
    static setOnTeamUpdateRevocationHandler(fn: () => void): void {
        BaseApiService._onTeamUpdateRevocationHandler = fn;
    }

    constructor(tokenRefreshFn: TokenRefreshFunction) {
        this.apiBaseUrl = config.apiBaseUrl;
        this.tokenRefreshFn = tokenRefreshFn;
    }

    /**
     * Builds a full API URL by appending the path to the base URL
     * @param path - The API path (e.g., '/v1/team/update')
     * @returns The complete URL
     */
    protected buildUrl(path: string): string {
        // Ensure path starts with '/' and doesn't have double slashes
        const cleanPath = path.startsWith('/') ? path : `/${path}`;
        return `${this.apiBaseUrl}${cleanPath}`;
    }

    /**
     * Checks if an error indicates a connection refused error
     * @param error - The error to check
     * @returns True if the error indicates connection refused
     */
    /**
     * Checks if a URL is the error generation test endpoint
     * This endpoint is expected to return various error codes for testing, so we shouldn't
     * trigger normal error handling (logout, maintenance mode, etc.)
     */
    private isErrorGenerationEndpoint(url: string | undefined): boolean {
        if (!url) return false;
        return url.includes('/admin/tests/generate-error');
    }

    /**
     * Public client expense flow uses link tokens in the URL, not app login.
     * Suppress 401 → /auth/login redirect (see handleResponse) for that flow so stale cookies
     * or infra 401s do not send clients to the attorney login page.
     */
    private shouldSuppressLoginRedirectOn401(originalUrl: string | undefined): boolean {
        if (typeof window === 'undefined') {
            return false;
        }
        if (window.location.pathname.startsWith('/client/expenses')) {
            return true;
        }
        const u = originalUrl || '';
        return u.includes('/v1/client/expenses');
    }

    private isConnectionRefusedError(error: any): boolean {
        if (!error) return false;

        // Check for common connection error messages
        const errorMessage = error.message?.toLowerCase() || '';
        const errorName = error.name?.toLowerCase() || '';

        // Network errors that indicate connection refused
        if (errorName === 'typeerror' && errorMessage.includes('failed to fetch')) {
            return true;
        }

        // Check for specific connection refused patterns
        if (errorMessage.includes('connection refused') ||
            errorMessage.includes('err_connection_refused') ||
            errorMessage.includes('networkerror') ||
            errorMessage.includes('network request failed')) {
            return true;
        }

        return false;
    }

    /**
     * Feeds this response's build identity to the stale-bundle detector.
     *
     * Every API response carries X-Calculist-Git-Commit (AppBuildHeaderMiddleware.cs), so ordinary
     * app traffic is enough to notice a deploy -- no polling required, which is what catches a
     * mobile tab whose background timers the OS has frozen. A 409 carrying X-Calculist-Stale-Client
     * is the API explicitly refusing a write from an out-of-date bundle.
     *
     * Assumes api and web are deployed together; a split deploy would read as a false mismatch,
     * which the reload-loop breaker in AppBuildManager degrades to a banner rather than a loop.
     */
    private noteBuildHeaders(response: Response): void {
        if (response.headers.get('X-Calculist-Stale-Client') === 'true') {
            appBuildManager.markStale();
            return;
        }
        appBuildManager.observeServerSha(response.headers.get('X-Calculist-Git-Commit'));
    }

    /**
     * Makes a fetch request with common configuration
     * @param url - The URL to request
     * @param options - Fetch options
     * @param retryOn401 - Whether to retry on 401 after token refresh (default: true)
     * @param requestBodyForLogging - Optional request body to attach to response for error logging
     * @returns Promise containing the response
     */
    protected async makeRequest(url: string, options: RequestInit = {}, retryOn401: boolean = true, requestBodyForLogging?: any): Promise<Response> {
        // Clear any existing error alerts before making a new request
        alertManager.clearErrorAlerts();

        const defaultOptions: RequestInit = {
            credentials: 'include', // Include cookies for authentication
            headers: {
                'Content-Type': 'application/json',
                // Diagnostic-only identifiers for log filtering (docs/AUTOSAVE_DETAILS.md §18) --
                // not used for auth/authorization. See utils/clientIdentifiers.ts.
                'X-Device-Id': getDeviceId(),
                'X-Tab-Id': getTabId(),
                // Build identity of this bundle, so the API can reject writes from a stale client
                // when the StaleClientGuard flag is on. Empty on local builds with no git sha.
                'X-Calculist-Client-Git-Commit': appBuildManager.getRunningSha(),
                ...options.headers,
            },
        };

        const mergedOptions = { ...defaultOptions, ...options };

        try {
            const response = await fetch(url, mergedOptions);
            this.noteBuildHeaders(response);

            // Attach request body and original URL to response for error logging and special handling
            if (requestBodyForLogging !== undefined) {
                (response as any).__requestBody = requestBodyForLogging;
            }
            (response as any).__originalUrl = url;

            // Check for maintenance status (503)
            // Skip maintenance mode handling for error generation test endpoint - it's expected to return 503
            if (response.status === 503 && !this.isErrorGenerationEndpoint(url)) {
                apiStatusManager.setMaintenance(true);
                this.storeRetryableRequest(url, mergedOptions);
                return response; // Return the 503 response so callers can handle it
            }

            // Sitewide gate (test/cert): the gate cookie is missing/expired. Re-show the password screen.
            if (response.status === SITE_GATE_STATUS) {
                siteGateManager.notifyLocked();
                return response;
            }

            // If we successfully got a response (and it's not 503), API is up
            apiStatusManager.setApiDown(false);
            apiStatusManager.setMaintenance(false);
            apiStatusManager.setLastFailedRequest(null);

            // If we get a 401 and retry is enabled, attempt automatic token refresh
            // Skip token refresh for error generation test endpoint - it's expected to return 401
            if (response.status === 401 && retryOn401 && this.shouldAttemptRefresh(url) && !this.isErrorGenerationEndpoint(url)) {
                const refreshSuccess = await this.attemptTokenRefresh();

                if (refreshSuccess) {
                    // Retry the original request once after successful refresh
                    // debugLog('[BaseApiService] Token refreshed, retrying original request');
                    try {
                        const retryResponse = await fetch(url, mergedOptions);
                        this.noteBuildHeaders(retryResponse);
                        // Attach request body and original URL to retry response for error logging and special handling
                        if (requestBodyForLogging !== undefined) {
                            (retryResponse as any).__requestBody = requestBodyForLogging;
                        }
                        (retryResponse as any).__originalUrl = url;
                        // Check for maintenance status
                        // Skip maintenance mode handling for error generation test endpoint - it's expected to return 503
                        if (retryResponse.status === 503 && !this.isErrorGenerationEndpoint(url)) {
                            apiStatusManager.setMaintenance(true);
                            this.storeRetryableRequest(url, mergedOptions);
                        } else {
                            apiStatusManager.setApiDown(false);
                            apiStatusManager.setMaintenance(false);
                            apiStatusManager.setLastFailedRequest(null);
                        }
                        return retryResponse;
                    } catch (retryError) {
                        if (this.isConnectionRefusedError(retryError)) {
                            // Store the failed request for retry
                            this.storeRetryableRequest(url, mergedOptions);
                            apiStatusManager.setApiDown(true);
                        }
                        throw retryError;
                    }
                }
            }

            return response;
        } catch (error) {
            // Check if this is a connection refused error
            if (this.isConnectionRefusedError(error)) {
                debugWarn('[BaseApiService] Connection refused - API appears to be down:', error);
                // Store the failed request for retry
                this.storeRetryableRequest(url, mergedOptions);
                apiStatusManager.setApiDown(true);
            } else {
                // For other errors, assume API is up (might be a different issue)
                apiStatusManager.setApiDown(false);
            }
            throw error;
        }
    }

    /**
     * Stores a failed request for the trouble/maintenance pages to retry, unless replaying it
     * would repeat a side effect.
     *
     * A payment POST that fails at the network layer may already have reached Stripe -- the client
     * cannot tell. Replaying it charges the card again, so those are dropped instead of stored.
     * The stored request is also cleared, so an earlier safe request is not retried in its place.
     */
    private storeRetryableRequest(url: string, options: RequestInit): void {
        if (!isReplayableRequest(url)) {
            debugWarn('[BaseApiService] Not storing a non-replayable request for retry:', url);
            apiStatusManager.setLastFailedRequest(null);
            return;
        }

        apiStatusManager.setLastFailedRequest({ url, options });
    }

    /**
     * Determines if we should attempt token refresh for a given URL
     * Excludes auth endpoints to avoid infinite loops.
     * 
     * Protection against infinite loops:
     * 1. This method excludes /auth/refresh endpoints
     * 2. refreshToken() method passes retryOn401=false
     * 3. Both conditions must be true: retryOn401 && shouldAttemptRefresh(url)
     * 
     * So even if this check passed, refreshToken's retryOn401=false would prevent loops.
     */
    private shouldAttemptRefresh(url: string): boolean {
        const authEndpoints = ['/auth/login', '/auth/logout', '/auth/refresh'];
        return !authEndpoints.some(endpoint => url.includes(endpoint));
    }

    /**
     * Attempts to refresh the access token
     * Uses a singleton pattern to ensure only one refresh happens at a time
     * @returns Promise<boolean> - True if refresh succeeded, false otherwise
     */
    private async attemptTokenRefresh(): Promise<boolean> {
        // Capture the function reference to avoid TypeScript issues in the async closure
        const refreshFn = this.tokenRefreshFn;

        // If refresh is already in progress, wait for it
        if (BaseApiService.refreshInProgress && BaseApiService.refreshPromise) {
            // debugLog('[BaseApiService] Token refresh already in progress, waiting...');
            return await BaseApiService.refreshPromise;
        }

        // Start a new refresh
        BaseApiService.refreshInProgress = true;
        BaseApiService.refreshPromise = (async () => {
            try {
                // debugLog('[BaseApiService] Attempting automatic token refresh...');
                // Call the provided token refresh function
                const success = await refreshFn();
                // debugLog('[BaseApiService] Token refresh successful');
                return success;
            } catch (error) {
                debugWarn('[BaseApiService] Token refresh failed:', error);
                return false;
            } finally {
                BaseApiService.refreshInProgress = false;
                BaseApiService.refreshPromise = null;
            }
        })();

        return await BaseApiService.refreshPromise;
    }

    /**
     * Determines if a payload should be gzipped based on its size
     * Uses configurable threshold from environment config
     * @param sizeInBytes - The size of the payload in bytes
     * @returns True if the data should be gzipped
     */
    private shouldGzip(sizeInBytes: number): boolean {
        // Otherwise, gzip if payload exceeds threshold
        return sizeInBytes > config.gzipThresholdBytes;
    }

    /**
     * Compresses data using gzip
     * Uses the Compression Streams API (available in modern browsers)
     * @param data - The data to compress (as string or Blob)
     * @returns Promise containing the compressed data as ArrayBuffer
     */
    private async compressData(data: string | Blob): Promise<ArrayBuffer> {
        // Check if Compression Streams API is available
        if (typeof CompressionStream === 'undefined') {
            throw new Error('Compression Streams API not available in this browser');
        }

        const stream = new CompressionStream('gzip');
        const writer = stream.writable.getWriter();

        try {
            if (typeof data === 'string') {
                const encoder = new TextEncoder();
                writer.write(encoder.encode(data));
            } else {
                // For Blob, read it as ArrayBuffer and write chunks
                const arrayBuffer = await data.arrayBuffer();
                writer.write(arrayBuffer);
            }
            writer.close();

            // Read compressed data
            const response = new Response(stream.readable);
            return await response.arrayBuffer();
        } catch (error) {
            writer.abort();
            throw error;
        }
    }

    /**
     * Serializes FormData and gets its metadata
     * @param formData - The FormData to serialize
     * @returns Promise containing the size, serialized Blob, and Content-Type
     */
    private async serializeFormData(formData: FormData): Promise<{ size: number; blob: Blob; contentType: string }> {
        // Use Request API to serialize FormData (this is what the browser does internally)
        const request = new Request('', {
            method: 'POST',
            body: formData,
        });

        // Try to get Content-Type before consuming the body
        // Note: Headers might not be populated until request is sent, so we have a fallback
        let contentType = request.headers.get('Content-Type') || 'multipart/form-data';

        // Get the serialized body as Blob (this consumes the FormData)
        const blob = await request.blob();

        return {
            size: blob.size,
            blob: blob,
            contentType: contentType,
        };
    }

    /**
     * Makes a POST request with JSON data
     * Automatically gzips large payloads (> 10 KB) to reduce bandwidth
     * @param path - The API path
     * @param data - The data to send
     * @param retryOn401 - Whether to retry on 401 after token refresh (default: true)
     * @returns Promise containing the response
     */
    protected async postJson<T>(path: string, data: T, retryOn401: boolean = true, extraHeaders?: Record<string, string>): Promise<Response> {
        const jsonString = JSON.stringify(data);
        const sizeInBytes = new Blob([jsonString]).size;

        // Store original data for error logging
        const requestBodyForLogging = data;

        let response: Response;

        // Check if we should gzip this payload
        if (this.shouldGzip(sizeInBytes)) {
            try {
                const compressedData = await this.compressData(jsonString);
                // debugLog(`[BaseApiService] Sending gzipped POST request to ${path} (original: ${(new Blob([jsonString]).size / 1024).toFixed(2)} KB, compressed: ${(compressedData.byteLength / 1024).toFixed(2)} KB)`);

                response = await this.makeRequest(this.buildUrl(path), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Content-Encoding': 'gzip',
                        ...extraHeaders,
                    },
                    body: compressedData,
                }, retryOn401, requestBodyForLogging);
            } catch (error) {
                // If compression fails (e.g., browser doesn't support it), fall back to uncompressed
                debugWarn('[BaseApiService] Failed to compress request body, falling back to uncompressed:', error);
                response = await this.makeRequest(this.buildUrl(path), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...extraHeaders },
                    body: jsonString,
                }, retryOn401, requestBodyForLogging);
            }
        } else {
            // Small payload, send uncompressed
            response = await this.makeRequest(this.buildUrl(path), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...extraHeaders },
                body: jsonString,
            }, retryOn401, requestBodyForLogging);
        }

        return response;
    }

    /**
     * Makes a POST request with FormData
     * Automatically gzips large payloads (> 10 KB) to reduce bandwidth
     * @param path - The API path
     * @param formData - The FormData to send
     * @param retryOn401 - Whether to retry on 401 after token refresh (default: true)
     * @returns Promise containing the response
     */
    protected async postFormData(path: string, formData: FormData, retryOn401: boolean = true): Promise<Response> {
        try {
            // Serialize FormData to get its size, serialized blob, and Content-Type
            const { size: sizeInBytes, blob: formDataBlob, contentType } = await this.serializeFormData(formData);

            // Store FormData for error logging (we'll log a note that it's FormData)
            const requestBodyForLogging = '[FormData - see network tab for details]';

            // Check if we should gzip this payload based on configured threshold
            if (this.shouldGzip(sizeInBytes)) {
                try {
                    // Compress the serialized FormData Blob
                    const compressedData = await this.compressData(formDataBlob);
                    // debugLog(`[BaseApiService] Sending gzipped FormData POST request to ${path} (original: ${(sizeInBytes / 1024).toFixed(2)} KB, compressed: ${(compressedData.byteLength / 1024).toFixed(2)} KB)`);

                    // Note: The boundary is embedded in the serialized FormData body,
                    // so the API can parse it after decompression even if Content-Type doesn't include the boundary

                    // The API's request decompression middleware will handle decompression transparently
                    // and ASP.NET Core's model binding will work with the decompressed multipart/form-data
                    return this.makeRequest(this.buildUrl(path), {
                        method: 'POST',
                        headers: {
                            'Content-Type': contentType,
                            'Content-Encoding': 'gzip',
                        },
                        body: compressedData,
                    }, retryOn401, requestBodyForLogging);
                } catch (error) {
                    // If compression fails (e.g., browser doesn't support it), fall back to uncompressed
                    debugWarn('[BaseApiService] Failed to compress FormData, falling back to uncompressed:', error);
                    return this.makeRequest(this.buildUrl(path), {
                        method: 'POST',
                        body: formData,
                        // Don't set Content-Type header for FormData - let the browser set it with boundary
                        headers: {},
                    }, retryOn401, requestBodyForLogging);
                }
            } else {
                // Small payload, send uncompressed
                return this.makeRequest(this.buildUrl(path), {
                    method: 'POST',
                    body: formData,
                    // Don't set Content-Type header for FormData - let the browser set it with boundary
                    headers: {},
                }, retryOn401, requestBodyForLogging);
            }
        } catch (error) {
            // If serialization fails, fall back to uncompressed
            debugWarn('[BaseApiService] Error serializing FormData, falling back to uncompressed:', error);
            return this.makeRequest(this.buildUrl(path), {
                method: 'POST',
                body: formData,
                // Don't set Content-Type header for FormData - let the browser set it with boundary
                headers: {},
            }, retryOn401, '[FormData - see network tab for details]');
        }
    }


    /**
     * Get current user info from sessionStorage (if available)
     * This is a utility function to get user info without React hooks
     */
    private getCurrentUserInfo(): { userId?: string; teamId?: string } {
        try {
            const authState = sessionStorage.getItem('auth_state');
            if (authState) {
                const parsed = JSON.parse(authState);
                if (parsed?.user) {
                    return {
                        userId: parsed.user.userId,
                        teamId: parsed.user.teamId,
                    };
                }
            }
        } catch {
            // Silently fail - can't access user info
        }
        return {};
    }

    /**
     * Sanitize sensitive fields in request body before logging
     * Masks passwords, tokens, and other sensitive data
     */
    private sanitizeRequestBody(body: any): any {
        if (!body || typeof body !== 'object') {
            return body;
        }

        // List of sensitive field names (case-insensitive matching)
        const sensitiveFields = [
            'password',
            'newPassword',
            'confirmPassword',
            'oldPassword',
            'currentPassword',
            'token',
            'refreshToken',
            'accessToken',
            'refresh_token',
            'access_token',
            'apiKey',
            'api_key',
            'apikey',
            'secret',
            'authToken',
            'sessionId',
            'session_id',
            'authorization',
            'bearer',
            'credentials',
        ];

        // Create a sanitized copy
        const sanitized = Array.isArray(body) ? [...body] : { ...body };

        for (const key in sanitized) {
            if (Object.prototype.hasOwnProperty.call(sanitized, key)) {
                const lowerKey = key.toLowerCase();

                // Check if this field should be sanitized
                const isSensitive = sensitiveFields.some(field =>
                    lowerKey === field.toLowerCase() ||
                    lowerKey.includes(field.toLowerCase())
                );

                if (isSensitive) {
                    // Mask the value
                    const value = sanitized[key];
                    if (typeof value === 'string' && value.length > 0) {
                        sanitized[key] = '[REDACTED]';
                    } else if (value !== null && value !== undefined) {
                        sanitized[key] = '[REDACTED]';
                    }
                } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
                    // Recursively sanitize nested objects
                    sanitized[key] = this.sanitizeRequestBody(sanitized[key]);
                }
            }
        }

        return sanitized;
    }

    /**
     * Checks if an HTTP status code should be logged as an error
     * Logs errors for status codes that are not: 2xx, 3xx, 400, 401, 402, 403, 429
     * @param status - HTTP status code
     * @returns True if the status should be logged
     */
    private shouldLogErrorStatus(status: number): boolean {
        // Don't log 2xx (success) or 3xx (redirect) codes
        if (status >= 200 && status < 400) {
            return false;
        }
        // Don't log 400 (Bad Request), 401 (Unauthorized), 402 (Payment Required, e.g. create-user's
        // SEAT_PAYMENT_REQUIRED), 403 (Forbidden), or 429 (Too Many Requests).
        // These are expected client errors that don't indicate server problems
        if (status === 400 || status === 401 || status === 402 || status === 403 || status === 429) {
            return false;
        }
        // Log everything else (404, 405-499 except above, 500-599, etc.)
        return true;
    }

    /**
     * Handles API response and throws appropriate errors
     * @param response - The fetch response
     * @param requestBody - Optional request body to log on error (for POST requests)
     * @returns Promise containing the parsed JSON response
     */
    protected async handleResponse<T>(response: Response): Promise<T> {
        if (!response.ok) {
            // Log errors for unexpected status codes (not 2xx, 3xx, 400, 401, 403)
            if (this.shouldLogErrorStatus(response.status)) {
                try {
                    // Try to get error details from response body
                    let errorMessage = `API Error ${response.status}: ${response.statusText}`;
                    let errorDetails: any = null;

                    try {
                        const responseClone = response.clone();
                        const errorData = await responseClone.json();
                        errorDetails = errorData;

                        // Extract error message from response
                        if (errorData.message) {
                            errorMessage = errorData.message;
                        } else if (errorData.error) {
                            errorMessage = typeof errorData.error === 'string'
                                ? errorData.error
                                : errorData.error.message || errorData.error.title || errorMessage;
                        } else if (errorData.title) {
                            errorMessage = errorData.title;
                            if (errorData.detail) {
                                errorMessage += `: ${errorData.detail}`;
                            }
                        }
                    } catch {
                        // If we can't parse JSON, try text
                        try {
                            const responseClone = response.clone();
                            const text = await responseClone.text();
                            if (text && text.length > 0 && text.length < 500) {
                                errorMessage = text;
                            }
                        } catch {
                            // Use default error message
                        }
                    }

                    // Create error object for logging
                    const apiError = new Error(errorMessage);

                    // Build stack trace with response details and request body (if available)
                    let stackTrace = `API Error ${response.status} ${response.statusText}\nURL: ${response.url}`;

                    // Include request body if available (for debugging what was sent)
                    const requestBody = (response as any).__requestBody;
                    if (requestBody !== undefined) {
                        try {
                            // Sanitize sensitive fields before logging
                            const sanitizedBody = this.sanitizeRequestBody(requestBody);

                            // Safely stringify request body (handle FormData, large objects, etc.)
                            let requestBodyStr: string;
                            if (sanitizedBody === '[FormData - see network tab for details]') {
                                requestBodyStr = sanitizedBody;
                            } else if (typeof sanitizedBody === 'string') {
                                // Truncate if too long
                                requestBodyStr = sanitizedBody.length > 5000
                                    ? sanitizedBody.substring(0, 5000) + '... [truncated]'
                                    : sanitizedBody;
                            } else {
                                // Stringify object, truncate if too long
                                const stringified = JSON.stringify(sanitizedBody, null, 2);
                                requestBodyStr = stringified.length > 5000
                                    ? stringified.substring(0, 5000) + '... [truncated]'
                                    : stringified;
                            }
                            stackTrace += `\nRequest Body: ${requestBodyStr}`;
                        } catch {
                            // If we can't stringify, just note that request body exists
                            stackTrace += `\nRequest Body: [Unable to serialize]`;
                        }
                    }

                    // Include response error details
                    if (errorDetails) {
                        const errorDetailsStr = JSON.stringify(errorDetails, null, 2);
                        stackTrace += `\nResponse Error: ${errorDetailsStr.length > 5000
                            ? errorDetailsStr.substring(0, 5000) + '... [truncated]'
                            : errorDetailsStr}`;
                    }

                    (apiError as any).stack = stackTrace;

                    // Get user info from sessionStorage
                    const userInfo = this.getCurrentUserInfo();

                    // Log to error logging service (will be rate-limited and deduplicated)
                    await logError(apiError, undefined, userInfo.userId, userInfo.teamId);
                } catch (loggingError) {
                    // Silently fail - don't let error logging cause more errors
                }
            }

            // If we get a 401 and we're not on an auth route, redirect to login
            // Skip redirect/logout for error generation test endpoint - it's expected to return 401
            const originalUrl = (response as any).__originalUrl || response.url;
            if (response.status === 401 && !this.isErrorGenerationEndpoint(originalUrl)) {
                // A revoked session gets an explanatory modal instead of a silent bounce to the
                // login page. 'team_update' covers subscription/billing/status changes made in
                // admin (which force every team member to sign in again); 'user_update' covers a
                // single user being force-logged-out.
                const revocationReason = response.headers.get('X-Revocation-Reason');
                if ((revocationReason === 'team_update' || revocationReason === 'user_update')
                    && BaseApiService._onTeamUpdateRevocationHandler) {
                    BaseApiService._onTeamUpdateRevocationHandler();
                    throw new Error(`Session revoked (${revocationReason})`);
                }

                const currentPath = window.location.pathname;
                const isAuthRoute = currentPath.startsWith('/auth/');

                if (!isAuthRoute && !this.shouldSuppressLoginRedirectOn401(originalUrl)) {
                    debugWarn('[BaseApiService] 401 received on non-auth route, redirecting to login:', currentPath);

                    // Clear auth state from sessionStorage before redirecting
                    // This ensures the login page doesn't think we're still authenticated
                    try {
                        sessionStorage.removeItem('auth_state');
                    } catch (e) {
                        debugWarn('[BaseApiService] Failed to clear auth_state from sessionStorage:', e);
                    }

                    // Store the current path for redirect after login
                    if (currentPath && currentPath !== '/auth/login') {
                        sessionStorage.setItem('redirectAfterLogin', currentPath + window.location.search);
                    }

                    // Redirect to login page
                    // Note: HTTP-only cookies cannot be cleared from JavaScript, but they'll be rejected
                    // by the server if they're invalid (expired, wrong tokenVersion, etc.)
                    window.location.href = '/auth/login';
                    // Return a rejected promise to stop further processing
                    throw new Error('Unauthorized - redirecting to login');
                }
            }

            // If we get a 403, call auth/me to refresh auth state
            // Skip redirect/logout for error generation test endpoint - it's expected to return 403
            if (response.status === 403 && !this.isErrorGenerationEndpoint(originalUrl)) {
                const currentPath = window.location.pathname;
                const isAuthRoute = currentPath.startsWith('/auth/');
                const isAdminRoute = currentPath.startsWith('/admin/');
                const responseUrl = response.url || '';
                const isAuthEndpoint = responseUrl.includes('/auth/');

                // If we're on an admin route and get a 403, redirect to home
                if (isAdminRoute) {
                    debugWarn('[BaseApiService] 403 received on admin route, redirecting to home:', currentPath);
                    window.location.href = '/';
                    throw new Error('Forbidden - redirecting to home');
                }

                // Don't call auth/me if we're already on an auth route or calling an auth endpoint
                // to avoid infinite loops
                if (!isAuthRoute && !isAuthEndpoint && BaseApiService._on403Handler) {
                    debugWarn('[BaseApiService] 403 received, calling auth/me to refresh auth state');
                    try {
                        await BaseApiService._on403Handler();
                        debugWarn('[BaseApiService] auth/me call completed after 403');
                    } catch (authError) {
                        debugWarn('[BaseApiService] auth/me call failed after 403:', authError);
                        // Continue with normal error handling even if auth/me fails
                    }
                }
            }

            let errorMessage = this.getDefaultErrorMessage(response.status);
            let errorDetails: any = null;

            try {
                // Clone response to read body without consuming it
                const responseClone = response.clone();
                const errorData = await responseClone.json();
                errorDetails = errorData;

                // Try multiple ways to extract error message
                // For 400 Bad Request, prioritize ModelState validation errors over generic title
                if (response.status === 400 && errorData.errors) {
                    // ASP.NET Core ModelState validation errors format
                    // Extract all validation error messages from all fields
                    const validationErrors: string[] = [];
                    if (typeof errorData.errors === 'object') {
                        for (const [key, value] of Object.entries(errorData.errors)) {
                            if (Array.isArray(value)) {
                                // Add each error message in the array
                                value.forEach((msg: string) => {
                                    validationErrors.push(msg);
                                });
                            } else if (typeof value === 'string') {
                                validationErrors.push(value);
                            }
                        }
                    }
                    if (validationErrors.length > 0) {
                        // Join all validation errors with semicolons for better readability in alerts
                        errorMessage = validationErrors.join('; ');
                    } else if (errorData.title) {
                        // Fallback to title if errors object is empty
                        errorMessage = errorData.title;
                    }
                } else if (errorData.message) {
                    errorMessage = errorData.message;
                } else if (errorData.error) {
                    // Handle both string and object error formats
                    errorMessage = typeof errorData.error === 'string'
                        ? errorData.error
                        : errorData.error.message || errorData.error.title || JSON.stringify(errorData.error);
                } else if (errorData.errors) {
                    // ASP.NET Core ModelState validation errors format (for non-400 status codes)
                    const validationErrors: string[] = [];
                    if (typeof errorData.errors === 'object') {
                        for (const [key, value] of Object.entries(errorData.errors)) {
                            if (Array.isArray(value)) {
                                // Add each error message in the array
                                value.forEach((msg: string) => {
                                    validationErrors.push(msg);
                                });
                            } else if (typeof value === 'string') {
                                validationErrors.push(value);
                            }
                        }
                    }
                    if (validationErrors.length > 0) {
                        // Join all validation errors with semicolons for better readability in alerts
                        errorMessage = validationErrors.join('; ');
                    } else if (errorData.title) {
                        errorMessage = errorData.title;
                    }
                } else if (errorData.title) {
                    // ASP.NET Core ProblemDetails format
                    errorMessage = errorData.title;
                    if (errorData.detail) {
                        errorMessage += `: ${errorData.detail}`;
                    }
                } else if (typeof errorData === 'string') {
                    errorMessage = errorData;
                } else if (errorData.status && errorData.title) {
                    // Another ProblemDetails variant
                    errorMessage = errorData.title;
                    if (errorData.detail) {
                        errorMessage += `: ${errorData.detail}`;
                    }
                } else {
                    // Last resort: try to stringify the error data if it's an object
                    if (typeof errorData === 'object' && Object.keys(errorData).length > 0) {
                        // Try to find any string value that might be an error message
                        const stringValues = Object.values(errorData)
                            .filter(v => typeof v === 'string' && v.length > 0)
                            .map(v => v as string);
                        if (stringValues.length > 0) {
                            errorMessage = stringValues[0];
                        } else {
                            // If we can't find a message, at least provide context
                            errorMessage = `${this.getDefaultErrorMessage(response.status)} (see console for details)`;
                        }
                    }
                }

                // Log full error details for debugging
                debugWarn('API Error Response:', {
                    status: response.status,
                    statusText: response.statusText,
                    errorData: errorData,
                    extractedMessage: errorMessage
                });
            } catch (parseError) {
                // If we can't parse the response body, use the default error message
                debugWarn('Could not parse error response body:', parseError);
                // Try to get response text as fallback
                try {
                    const text = await response.text();
                    if (text && text.length > 0 && text.length < 500) {
                        errorMessage = text;
                    }
                } catch (textError) {
                    debugWarn('Could not read error response as text:', textError);
                }
            }

            // BillingDunning: the team's grace period ended unpaid. Tell the app so it can refresh
            // the team's billing state and explain why the write was refused. Callers still get
            // the usual error below.
            if (response.status === 403 && errorDetails?.code === TEAM_BILLING_READ_ONLY_CODE) {
                try {
                    dispatchTeamBillingReadOnly({ message: errorDetails.message, graceEndsAt: errorDetails.graceEndsAt });
                } catch {
                    // Never let the notification break error handling.
                }
            }

            const error = new Error(errorMessage) as any;
            error.status = response.status;
            error.details = errorDetails;
            throw error;
        }

        return response.json();
    }

    /**
     * Gets a default error message based on HTTP status code
     * @param status - HTTP status code
     * @returns Default error message
     */
    private getDefaultErrorMessage(status: number): string {
        switch (status) {
            case 400:
                return 'Invalid request. Please check your input and try again.';
            case 401:
                return 'Authentication required. Please log in again.';
            case 403:
                return 'You do not have permission to perform this action.';
            case 404:
                return 'The requested resource was not found.';
            case 409:
                return 'Conflict: The resource has been modified by another process. Please refresh and try again.';
            case 422:
                return 'Validation error. Please check your input.';
            case 500:
                return 'An internal server error occurred. Please try again later.';
            case 503:
                return 'Service is temporarily unavailable. Please try again later.';
            default:
                return `An error occurred (HTTP ${status}). Please try again.`;
        }
    }
}
